/**
 * @module useReviewSchedule
 * Shared state for the review schedule/completion feature.
 *
 * Simplified model: review_assignments table replaces the old
 * review_rules + review_schedule + review_completions + review_copies tables.
 *
 * This composable handles:
 * - Screenshot upload (completion) via the assets service + reviews service
 * - Fetching assignment entries (calendar view compat)
 * - Deleting assignments
 * - Dialog state for the completion dialog
 * - PDF export hook for the calendar page
 */

import { ref } from "vue";
import api from "@/utils/api.js";
import { toLocalYM } from "@/utils/dateLocal.js";

// ── Schedule state (calendar compat) ──────────────────────────────────────────

// Assignment entries used by the calendar page — mapped to look like old schedule entries
const scheduleEntries = ref([]);
const scheduleLoading = ref(false);
const selectedMonth = ref(toLocalYM()); // YYYY-MM

// ── Dialog state ──────────────────────────────────────────────────────────────

const completionDialogOpen = ref(false);
const selectedScheduleEntry = ref(null);
const selectedReview = ref(null);

// Cross-component PDF export hook (set by ReviewCalendarPage on mount)
const exportCalendarPdf = ref(null);

// ── Snackbar ──────────────────────────────────────────────────────────────────

const snackbar = ref({ show: false, message: "", color: "success" });

const showSnack = (message, color = "success") => {
  snackbar.value = { show: true, message, color };
};

// ── Composable ────────────────────────────────────────────────────────────────

export function useReviewSchedule() {
  /**
   * Fetch all assignment entries from the admin endpoint.
   * Maps assignment_date → schedule_date for backward compat with calendar page.
   */
  const fetchSchedule = async (filters = {}) => {
    scheduleLoading.value = true;
    try {
      const params = new URLSearchParams();
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.platform) params.set("platform", filters.platform);
      if (filters.location) params.set("location", filters.location);
      if (filters.brand) params.set("brand", filters.brand);

      const query = params.toString();
      const res = await api.marketingApi.get(`/api/marketing/reviews/admin/assignments${query ? "?" + query : ""}`);
      // Map assignment_date to schedule_date for calendar compat
      const entries = (res.data.entries || []).map((e) => ({
        ...e,
        schedule_date: e.assignment_date,
        assigned_user_id: e.user_id,
      }));
      scheduleEntries.value = entries;
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      showSnack("Failed to fetch assignments", "error");
    } finally {
      scheduleLoading.value = false;
    }
  };

  /**
   * Delete a single assignment entry by ID.
   */
  const deleteEntry = async (id) => {
    try {
      await api.marketingApi.delete(`/api/marketing/reviews/admin/assignments/${id}`);
      showSnack("Entry deleted", "success");
    } catch (err) {
      showSnack("Failed to delete entry", "error");
      throw err;
    }
  };

  // ── Completion: Upload screenshot ─────────────────────────────────────────

  /**
   * Upload a screenshot proving the user completed a review assignment.
   *
   * Two-step process:
   * 1. Upload the image to the assets service (R2) — returns an asset ID
   * 2. POST the asset ID to the reviews service to complete the assignment
   */
  const uploadCompletion = async (assignmentId, file, reviewId = null, notes = null) => {
    try {
      // Step 1: Upload file to assets service
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset_type", "review_screenshot");
      formData.append("name", file.name);

      const uploadRes = await api.assetsApi.post("/api/assets", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const assetId = uploadRes.data.id;

      // Step 2: Complete the assignment with the asset ID
      const res = await api.marketingApi.post(`/api/marketing/reviews/assignments/${assignmentId}/complete`, {
        asset_id: assetId,
      });

      showSnack("Screenshot uploaded successfully", "success");
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to upload screenshot";
      showSnack(msg, "error");
      throw err;
    }
  };

  // ── Dialog controls ───────────────────────────────────────────────────────

  const openCompletionDialog = (entry, review = null) => {
    selectedScheduleEntry.value = entry;
    selectedReview.value = review;
    completionDialogOpen.value = true;
  };

  const closeCompletionDialog = () => {
    completionDialogOpen.value = false;
    selectedScheduleEntry.value = null;
    selectedReview.value = null;
  };

  return {
    // State
    scheduleEntries,
    scheduleLoading,
    selectedMonth,
    snackbar,

    // Dialog state
    completionDialogOpen,
    selectedScheduleEntry,
    selectedReview,

    // Methods
    fetchSchedule,
    deleteEntry,
    uploadCompletion,
    openCompletionDialog,
    closeCompletionDialog,
    showSnack,
    exportCalendarPdf,
  };
}
